import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Spin } from "antd";
import { message } from "utils/ciq-message";
import { SuccessMessages } from "constants/index";
import { MUTATION_SEND_FEATURE_WF_INVITE } from "services/graphQL/mutations";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import { ProjectContext } from "context/ProjectProvider";
import ErrorBoundary from "components/error-boundary";
import {
  IMaterial,
  IMaterialGroup,
  IMaterialGroupFormFields
} from "../type-definitions";
import useRequestDurationsMulti from "../hooks/request-durations-multi";
import HasAssigneeRhsContent from "./has-assignee-rhs-content";
import ExcludedMaterialsRhsContent from "./excluded-materials-rhs-content";
import { VerticalList, VerticalListItem } from "../widgets";
import Footer, { SendRequestsBtnDisabledReasons } from "./footer";
import "../request-durations.scss";

function getGroupById(groupId: string, groupedMaterials: any) {
  if (groupedMaterials?.completedWorkflowGroup?.groupId === groupId)
    return groupedMaterials?.completedWorkflowGroup;
  if (groupedMaterials?.unassignedGroup?.groupId === groupId)
    return groupedMaterials?.unassignedGroup;
  if (groupedMaterials?.unregisteredGroup?.groupId === groupId)
    return groupedMaterials?.unregisteredGroup;
  if (groupedMaterials?.inactiveUserGroup?.groupId === groupId)
    return groupedMaterials?.inactiveUserGroup;
  return groupedMaterials?.assignedGroups?.find(
    (group: IMaterialGroup) => group.groupId === groupId
  );
}

function RequestDurationsMulti(props: {
  selectedItems: { id: string }[];
  onCancel: () => void;
  onComplete: () => void;
}) {
  const { selectedItems, onCancel, onComplete } = props;

  const { data, loading } = useRequestDurationsMulti(selectedItems);

  const { gqlClientForProject } = useContext(ProjectContext);

  const [sendFeatureWFInvite] = useCIQMutation(
    MUTATION_SEND_FEATURE_WF_INVITE,
    {
      client: gqlClientForProject
    }
  );

  const [selectedTabKey, setSelectedTabKey] = useState<any>(null);

  const [groupFormFields, setGroupFormFields] =
    useState<IMaterialGroupFormFields>({});

  const [submitting, setSubmitting] = useState(false);

  const verticalListContainerRef = useRef<HTMLDivElement | null>(null);

  const unassignedUserGroups = useMemo(
    () => data?.groupedMaterials?.unassignedGroup,
    [data]
  );
  const unregisteredUserGroups = useMemo(
    () => data?.groupedMaterials?.unregisteredGroup,
    [data]
  );
  const assignedUserGroups = useMemo(
    () => data?.groupedMaterials?.assignedGroups,
    [data]
  );
  const inactiveUserGroups = useMemo(
    () => data?.groupedMaterials?.inactiveUserGroup,
    [data]
  );
  const completedWorkflowGroup = useMemo(
    () => data?.groupedMaterials?.completedWorkflowGroup,
    [data]
  );

  const emailsToBeSentCount = useMemo(() => {
    let totalCount = 0;
    assignedUserGroups?.forEach((group) => {
      totalCount +=
        group.materialsByStatus.allowedToSendRequestItems.length ||
        group.materialsByStatus.requestAlreadySentItems.length
          ? 1
          : // every assigned group has >=1 material, which filterMaterials partitions
            // into allowed|alreadySent, so at least one length is always > 0 -> never 0
            /* v8 ignore next */
            0;
    });
    return totalCount;
  }, [assignedUserGroups]);

  const excludedMaterials = useMemo(() => {
    const exMaterials: IMaterial[] = [];

    exMaterials.push(...(completedWorkflowGroup?.materials ?? []));

    exMaterials.push(...(unassignedUserGroups?.materials ?? []));

    exMaterials.push(...(unregisteredUserGroups?.materials ?? []));

    exMaterials.push(...(inactiveUserGroups?.materials ?? []));

    return exMaterials;
  }, [
    completedWorkflowGroup,
    unassignedUserGroups,
    unregisteredUserGroups,
    inactiveUserGroups
  ]);

  useEffect(() => {
    assignedUserGroups?.forEach((group) => {
      setGroupFormFields((prev) => ({
        ...prev,
        [group.groupId]: {
          note: ""
        }
      }));
    });
  }, [assignedUserGroups]);

  const verticalListItems = useMemo(() => {
    const listItems = [];

    if (assignedUserGroups) {
      const assignedTabs = assignedUserGroups?.map((group) => ({
        label: (
          <VerticalListItem
            label={group.subcontractorCompany?.name ?? ""}
            subLabel={`${group.assigneeUser?.first_name} ${group.assigneeUser?.last_name}`}
          />
        ),
        key: group.groupId
      }));
      listItems.push(...assignedTabs);
    }

    const hasExcludedGroups =
      completedWorkflowGroup ||
      unassignedUserGroups ||
      inactiveUserGroups ||
      unregisteredUserGroups;

    if (hasExcludedGroups) {
      const excludedTab = {
        label: <VerticalListItem label="Excluded Materials" hasWarningIcon />,
        key: "excluded_materials",
        hasTopSeparator: true
      };
      listItems.push(excludedTab);
    }

    return listItems;
  }, [
    assignedUserGroups,
    completedWorkflowGroup,
    unassignedUserGroups,
    unregisteredUserGroups,
    inactiveUserGroups
  ]);

  useEffect(() => {
    if (!selectedTabKey && verticalListItems.length > 0) {
      setSelectedTabKey(verticalListItems[0].key);
    }
  }, [selectedTabKey, verticalListItems]);

  const selectedGroup = useMemo(() => {
    if (selectedTabKey === "excluded_materials") {
      return null;
    }
    return getGroupById(selectedTabKey, data?.groupedMaterials);
  }, [data?.groupedMaterials, selectedTabKey]);

  const onSend = async () => {
    const sendEstimateInviteData: {
      assignee: string;
      notes: string;
      feature_instance_ids: string[];
    }[] = [];
    assignedUserGroups?.forEach((group) => {
      const allowedMaterialIds =
        group.materialsByStatus.allowedToSendRequestItems.map(
          (material) => material.id
        );

      const requestAlreadySentMaterialIds =
        group.materialsByStatus.requestAlreadySentItems.map(
          (material) => material.id
        );

      // every assigned group has >=1 material, partitioned into allowed|alreadySent,
      // so at least one id list is non-empty -> this if is always true (else unreachable)
      /* v8 ignore else */
      if (
        allowedMaterialIds.length > 0 ||
        requestAlreadySentMaterialIds.length > 0
      ) {
        sendEstimateInviteData.push({
          assignee: group.groupId,
          notes: groupFormFields[group.groupId]?.note.trim(),
          feature_instance_ids: allowedMaterialIds.concat(
            requestAlreadySentMaterialIds
          )
        });
      }
    });

    setSubmitting(true);
    const inviteResponse = await sendFeatureWFInvite({
      variables: {
        sendEstimateInviteData
      }
    });

    if (inviteResponse.success) {
      message.success(SuccessMessages.RequestSentSuccess);
      onComplete();
    }
    setSubmitting(false);
  };

  if (!data && loading) {
    return (
      <div
        className="h-full w-full flex items-center justify-center"
        data-testid="loading-spinner"
      >
        <Spin />
      </div>
    );
  }

  return (
    <div
      className="request-durations-multi w-full h-full overflow-hidden flex flex-col"
      data-testid="request-durations-multi-root"
    >
      <div className="grow flex overflow-hidden">
        <div
          ref={verticalListContainerRef}
          className="w-[30%] max-h-full overflow-y-auto border-0 border-r border-solid border-color-3"
          data-testid="vertical-list-container"
        >
          <VerticalList
            items={verticalListItems}
            onChange={setSelectedTabKey}
            activeKey={selectedTabKey}
            data-testid="vertical-list"
          />
        </div>
        <div
          className="w-[70%] max-h-full overflow-hidden p-4"
          data-testid="rhs-content-container"
        >
          {selectedTabKey === "excluded_materials" && (
            <div className="h-full min-h-0 overflow-y-auto">
              <ExcludedMaterialsRhsContent
                completedWorkflowGroup={completedWorkflowGroup}
                unassignedGroup={unassignedUserGroups}
                inactiveUserGroup={inactiveUserGroups}
                unregisteredGroup={unregisteredUserGroups}
              />
            </div>
          )}
          {selectedGroup?.groupType === "assignee_actual" && (
            <ErrorBoundary>
              <HasAssigneeRhsContent
                key={selectedGroup.groupId}
                group={selectedGroup}
                formFieldsState={[groupFormFields, setGroupFormFields]}
                data-testid="has-assignee-content"
              />
            </ErrorBoundary>
          )}
        </div>
      </div>
      <div
        className="border-0 border-t border-solid border-color-3"
        data-testid="footer-container"
      >
        <Footer
          onCancel={onCancel}
          onSend={onSend}
          data={{
            selectedCount: selectedItems.length,
            excludedCount: excludedMaterials.length,
            // emailsToBeSentCount memo always returns a number (totalCount), so ?? 0 fallback can never fire
            /* v8 ignore next */
            emailsToBeSentCount: emailsToBeSentCount ?? 0
          }}
          submitting={submitting}
          sendRequestsBtnProps={{
            disabled: emailsToBeSentCount === 0,
            "data-testid": "send-requests-btn"
          }}
          sendRequestsBtnDisabledReason={
            emailsToBeSentCount === 0
              ? SendRequestsBtnDisabledReasons.NO_VALID_MATERIAL
              : null
          }
          onExcludedClick={() => {
            setSelectedTabKey("excluded_materials");
            // ref is on an always-rendered div; current is always set once Footer (which fires this) is mounted
            /* v8 ignore next */
            if (verticalListContainerRef.current) {
              verticalListContainerRef.current.scrollTo({
                top: verticalListContainerRef.current.scrollHeight,
                behavior: "smooth"
              });
            }
          }}
        />
      </div>
    </div>
  );
}

export default RequestDurationsMulti;
